/**
 * The wait before a page of a queue run is read again.
 *
 * Exponential in the attempt, with jitter, so a room of billers hitting the
 * same blip does not retry in step: 300 ms, doubled per attempt, scaled by
 * a random factor between 0.4 and 1.4 — the shape RTK Query's own default
 * uses. An abort ends the wait at once, so a cache entry removed mid-backoff
 * does not hold a timer open for nobody.
 */
export const QUEUE_RUN_BACKOFF_BASE_MS = 300;

/** Attempt 1 waits 240–840 ms, attempt 2 twice that, attempt 3 twice again. */
export function queueRunBackoffDelayMs(
  attempt: number,
  random: () => number = Math.random,
): number {
  return Math.floor((random() + 0.4) * (QUEUE_RUN_BACKOFF_BASE_MS << Math.max(1, attempt)));
}

export function queueRunBackoff(
  attempt: number,
  _maxRetries?: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new Error('Aborted'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, queueRunBackoffDelayMs(attempt));
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
  });
}
